import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { api } from "../../api";
import usePageTitle from "../../hooks/usePageTitle";
import AppShell from "../AppShell";
import ImageWithFallback from "../ImageWithFallback";
import { buildItemUrl } from "../../utils/itemUrl";
import { formatPrice } from "../../utils/price";
import { PriceVisibilityBoundary } from "../../contexts/PriceVisibilityContext";

const FIRST_PARTNER_VOL_1_NUMBERS = new Set(["037", "038", "039", "040", "041", "042", "043", "044", "045"]);
const FIRST_PARTNER_VOL_2_NUMBERS = new Set(["046", "047", "048", "049", "050", "051", "052", "053", "054"]);

const panelSx = {
  p: { xs: 2, md: 2.5 },
  borderRadius: 2.5,
  border: "1px solid",
  borderColor: "rgba(12, 35, 64, 0.08)",
  backgroundColor: "#ffffff",
  boxShadow: "0 12px 30px rgba(15, 23, 42, 0.06)",
};

const cardSx = {
  display: "flex",
  flexDirection: "column",
  height: "100%",
  p: 1.5,
  borderRadius: 2.25,
  border: "1px solid",
  borderColor: "rgba(12, 35, 64, 0.08)",
  backgroundColor: "#fbfcff",
  textDecoration: "none",
  color: "inherit",
  transition: "transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease, background-color 160ms ease",
  "&:hover": {
    transform: "translateY(-3px)",
    boxShadow: "0 16px 30px rgba(15, 23, 42, 0.12)",
    borderColor: "rgba(38, 102, 208, 0.20)",
    backgroundColor: "#ffffff",
  },
};

function normalizeText(value) {
  return String(value || "").trim().toLowerCase();
}

function normalizeNumber(value) {
  return String(value || "").trim().padStart(3, "0");
}

function getPrimaryImage(item) {
  return item?.image_url || item?.ref_image_url || "";
}

function isPokemonOrTcgItem(item) {
  const category = normalizeText(item?.category);

  return (
    category === "pokemon cards" ||
    category === "pokemon card" ||
    category === "tcg card" ||
    category === "trading card"
  );
}

function getFirstPartnerSearchText(item) {
  return [
    item?.name,
    item?.set_name,
    item?.series_collection,
    item?.edition_variant,
    item?.brand,
    item?.category,
  ]
    .map(normalizeText)
    .join(" ");
}

function isFirstPartnerVol1Item(item) {
  const name = normalizeText(item?.name);
  const setName = normalizeText(item?.set_name);
  const series = normalizeText(item?.series_collection);
  const number = normalizeNumber(item?.number);
  const combined = getFirstPartnerSearchText(item);

  const matchesLegacyThirty =
    name.includes("30th anniversary") ||
    setName.includes("30th anniversary") ||
    series.includes("30th anniversary");

  const matchesPromoGrid =
    setName === "promo" &&
    series === "pokemon tcg" &&
    FIRST_PARTNER_VOL_1_NUMBERS.has(number);

  const matchesVol1Text =
    combined.includes("first partner") &&
    (
      combined.includes("vol. 1") ||
      combined.includes("volume 1") ||
      combined.includes("vol 1") ||
      combined.includes("series 1")
    );

  return isPokemonOrTcgItem(item) && (matchesPromoGrid || matchesVol1Text || (matchesLegacyThirty && FIRST_PARTNER_VOL_1_NUMBERS.has(number)));
}

function isFirstPartnerVol2Item(item) {
  const setName = normalizeText(item?.set_name);
  const series = normalizeText(item?.series_collection);
  const number = normalizeNumber(item?.number);
  const combined = getFirstPartnerSearchText(item);

  const matchesPromoGrid =
    setName === "promo" &&
    series === "pokemon tcg" &&
    FIRST_PARTNER_VOL_2_NUMBERS.has(number);

  const matchesVol2Text =
    combined.includes("first partner") &&
    (
      combined.includes("vol. 2") ||
      combined.includes("volume 2") ||
      combined.includes("vol 2") ||
      combined.includes("series 2")
    );

  return isPokemonOrTcgItem(item) && (matchesPromoGrid || matchesVol2Text);
}

function dedupeItemsById(rows) {
  return Array.from(
    new Map(
      rows.map((item) => [Number(item?.item_id || 0), item])
    ).values()
  ).filter((item) => Number(item?.item_id || 0) > 0);
}

function sortItemsByNumberThenId(rows) {
  return rows
    .slice()
    .sort((a, b) => {
      const aNumber = normalizeNumber(a?.number);
      const bNumber = normalizeNumber(b?.number);
      const aHasNumber = Boolean(String(a?.number || "").trim());
      const bHasNumber = Boolean(String(b?.number || "").trim());

      if (aHasNumber !== bHasNumber) return aHasNumber ? -1 : 1;
      if (aHasNumber && bHasNumber) {
        const numberDiff = aNumber.localeCompare(bNumber);
        if (numberDiff !== 0) return numberDiff;
      }
      return Number(a?.item_id || 0) - Number(b?.item_id || 0);
    });
}

function AnniversaryCard({ item }) {
  const imageSrc = getPrimaryImage(item);

  return (
    <Box component={RouterLink} to={buildItemUrl(item)} sx={cardSx}>
      <Box
        sx={{
          height: 155,
          borderRadius: 1.75,
          overflow: "hidden",
          backgroundColor: "#eef3f8",
          mb: 1.4,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {imageSrc ? (
          <ImageWithFallback
            src={item.image_url}
            fallbackSrcs={[item.ref_image_url]}
            alt={item.name || "Pokemon 30th Anniversary item"}
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
        ) : (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "text.secondary",
              fontWeight: 700,
            }}
          >
            No Image
          </Box>
        )}
      </Box>

      <Stack spacing={0.55} sx={{ minHeight: 88 }}>
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 800,
            color: "#10233f",
            lineHeight: 1.3,
            wordBreak: "break-word",
          }}
        >
          {item.name}
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 600 }}>
          {item?.set_name || item?.series_collection || (item?.number ? `No. ${item.number}` : "Pokemon 30th Anniversary")}
        </Typography>
        <PriceVisibilityBoundary><Typography variant="body2" sx={{ color: "primary.main", fontWeight: 800 }}>
          {formatPrice(item) || "TBD"}
        </Typography></PriceVisibilityBoundary>
      </Stack>
    </Box>
  );
}

export default function Pokemon30thAnniversaryPage() {
  usePageTitle("Pokemon 30th Anniversary");

  const [items, setItems] = useState([]);
  const [firstPartnerVol2Items, setFirstPartnerVol2Items] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;

    const loadItems = async () => {
      try {
        setLoading(true);
        setError("");

        const { data } = await api.get("/api/search", {
          params: {
            q: "Pokemon Promo",
            limit: 200,
          },
        });

        const { data: firstPartnerVol2Data } = await api.get("/api/search", {
          params: {
            q: "First Partner",
            limit: 200,
          },
        });

        if (!mounted) return;

        const rows = Array.isArray(data?.items) ? data.items : [];
        const filtered = rows.filter(isFirstPartnerVol1Item);
        const ordered = sortItemsByNumberThenId(dedupeItemsById(filtered));

        const firstPartnerVol2Rows = Array.isArray(firstPartnerVol2Data?.items) ? firstPartnerVol2Data.items : [];
        const firstPartnerVol2Ordered = sortItemsByNumberThenId(
          dedupeItemsById(firstPartnerVol2Rows.filter(isFirstPartnerVol2Item))
        );

        setItems(ordered);
        setFirstPartnerVol2Items(firstPartnerVol2Ordered);
      } catch (err) {
        if (!mounted) return;
        setError(err?.response?.data?.message || err?.message || "Failed to load Pokemon 30th Anniversary items.");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadItems();
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <AppShell>
      <Box sx={{ px: { xs: 0, md: 1 }, py: { xs: 1, md: 1.5 } }}>
        <Stack spacing={{ xs: 2.5, md: 3 }}>
          <Paper
            elevation={0}
            sx={{
              ...panelSx,
              p: { xs: 2.5, md: 3.25 },
              background: "linear-gradient(135deg, #18304f 0%, #255a8a 58%, #f2c55b 100%)",
              borderColor: "rgba(255,255,255,0.10)",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                background:
                  "radial-gradient(circle at top right, rgba(255,255,255,0.16), transparent 30%), radial-gradient(circle at bottom left, rgba(255,255,255,0.10), transparent 26%)",
                pointerEvents: "none",
              }}
            />
            <Box sx={{ position: "relative", zIndex: 1 }}>
              <Typography
                variant="overline"
                sx={{
                  display: "block",
                  color: "rgba(255,255,255,0.72)",
                  fontWeight: 800,
                  letterSpacing: 1.15,
                  mb: 1,
                }}
              >
                Anniversary Spotlight
              </Typography>
              <Typography
                variant="h3"
                sx={{
                  color: "#ffffff",
                  fontWeight: 900,
                  lineHeight: 1.08,
                  mb: 1.1,
                  maxWidth: 760,
                  fontSize: { xs: "2rem", md: "2.65rem" },
                }}
              >
                Pokemon 30th Anniversary
              </Typography>
              <Typography
                sx={{
                  color: "rgba(255,255,255,0.82)",
                  maxWidth: 760,
                  lineHeight: 1.65,
                  fontSize: { xs: "0.98rem", md: "1.02rem" },
                }}
              >
                A curated look at Pokemon 30th Anniversary items currently stored in the database.
              </Typography>
            </Box>
          </Paper>

          {loading ? (
            <Paper elevation={0} sx={{ ...panelSx, py: { xs: 5, md: 7 } }}>
              <Stack spacing={1.5} alignItems="center" justifyContent="center">
                <CircularProgress size={34} />
                <Typography variant="body1" sx={{ color: "text.secondary" }}>
                  Loading Pokemon 30th Anniversary items...
                </Typography>
              </Stack>
            </Paper>
          ) : error ? (
            <Paper elevation={0} sx={panelSx}>
              <Alert severity="error" sx={{ borderRadius: 2 }}>
                {error}
              </Alert>
            </Paper>
          ) : (
            <>
              <Paper elevation={0} sx={panelSx}>
                <Box sx={{ mb: 2.2 }}>
                  <Typography variant="h5" sx={{ fontWeight: 900, color: "#10233f", mb: 0.5 }}>
                    First Partner Collection Vol. 1
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary", maxWidth: 820, lineHeight: 1.65 }}>
                    Track Pokémon First Partner Collection Vol. 1 items from the anniversary lineup, with direct links to each item detail page.
                  </Typography>
                </Box>

                {items.length === 0 ? (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 3,
                      borderRadius: 2,
                      borderColor: "rgba(12, 35, 64, 0.08)",
                      backgroundColor: "#f9fbfd",
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#10233f", mb: 0.5 }}>
                      No First Partner Collection Vol. 1 items found
                    </Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      First Partner Collection Vol. 1 items are not currently available from the database response.
                    </Typography>
                  </Paper>
                ) : (
                  <Grid container spacing={2}>
                    {items.map((item) => (
                      <Grid item xs={12} sm={6} md={4} key={item.item_id}>
                        <AnniversaryCard item={item} />
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Paper>

              <Paper elevation={0} sx={panelSx}>
                <Box sx={{ mb: 2.2 }}>
                  <Typography variant="h5" sx={{ fontWeight: 900, color: "#10233f", mb: 0.5 }}>
                    First Partner Collection Vol. 2
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary", maxWidth: 820, lineHeight: 1.65 }}>
                    Track Pokémon First Partner Collection Vol. 2 items from the anniversary lineup, with direct links to each item detail page.
                  </Typography>
                </Box>

                {firstPartnerVol2Items.length === 0 ? (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 3,
                      borderRadius: 2,
                      borderColor: "rgba(12, 35, 64, 0.08)",
                      backgroundColor: "#f9fbfd",
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#10233f", mb: 0.5 }}>
                      No First Partner Collection Vol. 2 items found
                    </Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      First Partner Collection Vol. 2 items are not currently available from the database response.
                    </Typography>
                  </Paper>
                ) : (
                  <Grid container spacing={2}>
                    {firstPartnerVol2Items.map((item) => (
                      <Grid item xs={12} sm={6} md={4} key={item.item_id}>
                        <AnniversaryCard item={item} />
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Paper>
            </>
          )}
        </Stack>
      </Box>
    </AppShell>
  );
}
